import { Button, Input, Label, Logo, Wordmark } from "@qn/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fingerprint } from "lucide-react";
import { type FormEvent, useState } from "react";
import { api, type MeResponse } from "../lib/api";
import { useT } from "../lib/i18n";
import { passkeysSupported, signInWithPasskey } from "../lib/passkeys";
import { QuickToggles } from "./QuickToggles";

/** Sign-in buttons appear only for providers the owner has configured. */
const SOCIAL: Array<[string, string]> = [
  ["google", "Google"],
  ["apple", "Apple"],
  ["microsoft", "Microsoft (Outlook)"],
  ["github", "GitHub"],
  ["gitlab", "GitLab"],
  ["discord", "Discord"],
];

export function AuthScreen({ me }: { me?: MeResponse }) {
  const t = useT();
  const qc = useQueryClient();
  const setup = me?.needsSetup ?? false;
  const [mode, setMode] = useState<"login" | "signup">(setup ? "signup" : "login");
  const [form, setForm] = useState({ email: "", name: "", password: "" });

  const [ticket, setTicket] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const oauth = useQuery({
    queryKey: ["oauth-providers"],
    queryFn: () => api<Record<string, boolean>>("/auth/oauth/providers"),
    staleTime: 0,
  });
  const authError = new URLSearchParams(window.location.search).get("auth_error");
  const submit = useMutation({
    mutationFn: async (): Promise<{ needs2fa?: boolean; ticket?: string }> => {
      if (ticket) return api("/auth/login/2fa", { method: "POST", body: { ticket, code } });
      return mode === "signup"
        ? api("/auth/signup", { method: "POST", body: form })
        : api("/auth/login", {
            method: "POST",
            body: { email: form.email, password: form.password },
          });
    },
    onSuccess: (r) => {
      if (r?.needs2fa && r.ticket) setTicket(r.ticket);
      else void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
  const passkey = useMutation({
    mutationFn: signInWithPasskey,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["me"] }),
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit.mutate();
  };
  const signingUp = mode === "signup";

  return (
    <div className="grid min-h-dvh place-items-center p-4">
      <div className="fixed end-3 top-3 z-10">
        <QuickToggles />
      </div>
      <div className="animate-rise w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <Logo size={52} />
          <h1>
            <Wordmark size="lg" />
          </h1>
          <span className="text-xs tracking-widest text-[var(--fg-faint)]">by QN Ventures</span>
          <p className="text-sm text-[var(--fg-muted)]">{t("auth.tagline")}</p>
        </div>
        <form
          onSubmit={onSubmit}
          className="glass space-y-4 rounded-[var(--radius)] border border-[var(--line)] p-6 shadow-[var(--shadow)]"
        >
          {setup && (
            <p className="rounded-lg bg-[var(--accent-soft)] p-3 text-xs text-[var(--accent-text)]">
              {t("auth.firstRun")}
            </p>
          )}
          {ticket ? (
            <div>
              <Label htmlFor="code" hint={t("auth.codeHint")}>
                {t("auth.code")}
              </Label>
              <Input
                id="code"
                dir="ltr"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
              />
            </div>
          ) : (
            <>
              {signingUp && (
                <div>
                  <Label htmlFor="name">{t("auth.name")}</Label>
                  <Input
                    id="name"
                    autoComplete="name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  dir="ltr"
                  autoComplete="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="password" hint={signingUp ? t("auth.passwordHint") : undefined}>
                  {t("auth.password")}
                </Label>
                <Input
                  id="password"
                  type="password"
                  dir="ltr"
                  autoComplete={signingUp ? "new-password" : "current-password"}
                  required
                  minLength={signingUp ? 10 : 1}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
            </>
          )}
          {authError && !submit.error && (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {t("auth.oauthError", { e: authError })}
            </p>
          )}
          {submit.error && (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {submit.error.message}
            </p>
          )}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={submit.isPending}
          >
            {signingUp ? t("auth.createAccount") : t("auth.signIn")}
          </Button>
          {!ticket && !signingUp && passkeysSupported() && (
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full"
              loading={passkey.isPending}
              onClick={() => passkey.mutate()}
            >
              <Fingerprint className="size-5" /> {t("auth.passkey")}
            </Button>
          )}
          {passkey.error && (
            <p role="alert" className="text-center text-sm text-[var(--danger)]" dir="auto">
              {passkey.error.message}
            </p>
          )}
          {!ticket && SOCIAL.some(([k]) => oauth.data?.[k]) && (
            <div className="space-y-2 pt-1">
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-[var(--fg-faint)]">
                <span className="h-px flex-1 bg-[var(--line)]" />
                {t("auth.or")}
                <span className="h-px flex-1 bg-[var(--line)]" />
              </div>
              {SOCIAL.filter(([k]) => oauth.data?.[k]).map(([k, label]) => (
                <a
                  key={k}
                  href={`/api/auth/oauth/${k}/start`}
                  className="flex h-10 items-center justify-center gap-2 rounded-[var(--radius-sm)] border border-[var(--line-strong)] text-sm font-medium hover:bg-[var(--surface-2)]"
                >
                  {label}
                </a>
              ))}
            </div>
          )}
          {!setup && !ticket && me?.signupOpen && (
            <button
              type="button"
              className="w-full text-center text-sm text-[var(--fg-muted)] hover:text-[var(--fg)]"
              onClick={() => setMode(signingUp ? "login" : "signup")}
            >
              {signingUp ? t("auth.haveAccount") : t("auth.noAccount")}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
